/**
 * PinsTab - Pinned pages tab for NavigationCustomization
 * Search and manage pinned pages for cross-hub quick access
 */
import { Search, Pin, PinOff, X } from 'lucide-react'
import { HUB_REGISTRY } from '../../lib/hubRegistry'
import type { PinnedNavItem, HubId } from '../types'

interface PageOption {
  hubId: HubId
  hubName: string
  pageId: string
  pageLabel: string
  href: string
  icon: React.ComponentType<{ className?: string }>
}

interface PinsTabProps {
  searchQuery: string
  onSearchChange: (query: string) => void
  filteredPages: PageOption[]
  pinnedPages: PinnedNavItem[]
  onTogglePin: (page: PageOption) => void
  onRemovePin: (href: string) => void
}

export function PinsTab({
  searchQuery,
  onSearchChange,
  filteredPages,
  pinnedPages,
  onTogglePin,
  onRemovePin,
}: PinsTabProps) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500 dark:text-slate-400">
        Pin frequently used pages for quick access across all hubs.
      </p>
      
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          placeholder="Search pages to pin..."
          value={searchQuery}
          onChange={e => onSearchChange(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      </div>
      
      {/* Search Results */}
      {searchQuery && (
        <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
          {filteredPages.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-400">
              No pages found
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-48 overflow-y-auto">
              {filteredPages.slice(0, 10).map(page => {
                const isPinned = pinnedPages.some(p => p.href === page.href)
                const PageIcon = page.icon
                
                return (
                  <button
                    key={page.href}
                    onClick={() => onTogglePin(page)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800/50 text-left"
                  >
                    <PageIcon className="w-4 h-4 text-slate-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                        {page.pageLabel}
                      </p>
                      <p className="text-xs text-slate-400 truncate">
                        {page.hubName}
                      </p>
                    </div>
                    {isPinned ? (
                      <PinOff className="w-4 h-4 text-teal-500" />
                    ) : (
                      <Pin className="w-4 h-4 text-slate-400" />
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}
      
      {/* Current Pins */}
      <div>
        <h3 className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
          Your Pinned Pages ({pinnedPages.length})
        </h3>
        {pinnedPages.length === 0 ? (
          <div className="p-6 text-center border border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
            <Pin className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No pinned pages yet</p>
            <p className="text-xs text-slate-400 mt-1">Search above to add quick shortcuts</p>
          </div>
        ) : (
          <div className="space-y-2">
            {pinnedPages.map(pin => {
              const hub = HUB_REGISTRY[pin.sourceHub]
              const PinIcon = pin.icon
              
              return (
                <div
                  key={pin.href}
                  className="flex items-center gap-3 px-4 py-2.5 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-xl"
                >
                  <PinIcon className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      {pin.label}
                    </p>
                    <p className="text-xs text-slate-500">{hub?.name}</p>
                  </div>
                  <button
                    onClick={() => onRemovePin(pin.href)}
                    className="p-1 text-slate-400 hover:text-red-500 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
